import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { orderMsg, t } from '../../common/i18n/index.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { getDefaultDeliveryOrder } from '../../common/utils/orderUtils';
import { CompanyInfoResponse, CustomerOrderStatus } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { BreadCrumbsWithTitle } from '../BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { Loading } from '../Loading/index.js';
import { ValidationMessage } from '../ValidationMessage/ValidationMessage';
import { CustomerOrderFooter } from './CustomerOrderDetailsFooter.js';
import { EOECustomerOrderContent } from './EOECustomerOrderDetailsContent';
import type { CustomerOrderDetailsFormValues } from './CustomerOrderDetails.js';
import type { ContactPerson, CustomerOrder, Subscription } from '../../generated/api/models.js';
import type { BreadCrumbList } from '../BreadCrumbs/index.js';

import './CustomerOrderDetails.scss';

export interface EOECustomerOrderDetailsProps {
  pathTemplate: string;
  ordererContact?: ContactPerson;
  deviceSubscriptions: Subscription[];
  customerOrder?: CustomerOrder;
  customerType?: CompanyInfoResponse.CustomerTypeEnum;
  firstBreadCrumbName?: string;
  isApprover: boolean;
  billingAccountId?: string;
  costCenter?: string;
  employeeNumber?: string;
  breadCrumbPaths?: BreadCrumbList;
}

export const EOECustomerOrderDetails = ({
  pathTemplate,
  ordererContact,
  deviceSubscriptions,
  customerOrder,
  breadCrumbPaths,
  customerType,
  isApprover,
}: EOECustomerOrderDetailsProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [showMandatoryCheckboxError, setShowMandatoryCheckboxError] = useState(false);
  const showFooter = customerOrder?.status === CustomerOrderStatus.PENDING_APPROVAL && isApprover;
  const { authenticatedUser } = useAuth();

  const companyName = authenticatedUser?.companyName;

  const isInternalCustomer = customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;

  const defaultDeliveryOrder = getDefaultDeliveryOrder(customerOrder?.deliveryOrders ?? []);

  const methods = useForm<CustomerOrderDetailsFormValues>({
    defaultValues: {
      billingAccountId: customerOrder?.approverMustSelectBillingAccount ? undefined : customerOrder?.billingAccountId,
      employeeNumber: defaultDeliveryOrder?.subscriptionReference || '',
      costCenter: defaultDeliveryOrder?.costCenter || '',
    },
    mode: 'all',
  });

  useEffect(() => {
    if (customerOrder) {
      const deliveryOrder = getDefaultDeliveryOrder(customerOrder.deliveryOrders);
      methods.reset({
        billingAccountId: customerOrder.approverMustSelectBillingAccount ? undefined : customerOrder.billingAccountId,
        employeeNumber: deliveryOrder?.subscriptionReference || '',
        costCenter: deliveryOrder?.costCenter || '',
      });
    }
  }, [customerOrder, methods]);

  if (!customerOrder) {
    return <Loading />;
  }

  const onTriggerMandatoryCheckboxError = () => {
    setShowMandatoryCheckboxError(true);
  };

  const content: JSX.Element = (
    <>
      <EOECustomerOrderContent
        pathTemplate={pathTemplate}
        customerOrder={customerOrder}
        ordererContact={ordererContact}
        deviceSubscriptions={deviceSubscriptions}
        isInternalCustomer={isInternalCustomer}
        isApprover={isApprover}
      />
      {showFooter && (
        <>
          <CL.Checkbox
            checked={isChecked}
            onChange={() => {
              setIsChecked(!isChecked);
              setShowMandatoryCheckboxError(false);
            }}
          >
            {t.WO28('I have checked the validity of the billing and order details.')}
          </CL.Checkbox>
          {showMandatoryCheckboxError && (
            <ValidationMessage message={t.ILA7('Confirm you have reviewed the order and billing details')} />
          )}

          <CustomerOrderFooter
            customerOrder={customerOrder}
            ordererContact={ordererContact}
            isChecked={isChecked}
            onTriggerMandatoryCheckboxError={onTriggerMandatoryCheckboxError}
          />
        </>
      )}
    </>
  );

  const customerOrderDisplayId = customerOrder ? customerOrder.customerOrderDisplayId : '…';

  return (
    <FormProvider {...methods}>
      <DetailsWrapper
        classes={['of-customer-order-details']}
        detailsTop={breadCrumbPaths && <BreadCrumbsWithTitle breadCrumbPaths={breadCrumbPaths} />}
        id={`customer-order-details-${customerOrderDisplayId}`}
        heading={customerOrderDisplayId}
        headingBottom={`${formatTimestampToDDMMYYYY(customerOrder.created)}${companyName ? ` | ${companyName}` : ''}`}
        headingTop={t.C001(orderMsg)}
        heroPicto="order"
      >
        {content}
      </DetailsWrapper>
    </FormProvider>
  );
};
